import React from 'react'
import { TextField } from '@mui/material'
import { useField } from 'formik'
import Autocomplete from '@mui/material/Autocomplete'
import { isNil } from 'lodash-es'

export interface listOption {
  value: unknown
  label: string
}

export interface AutoCompleteFieldProps {
  name: string
  label: string
  options: listOption[]
  value?: unknown
  disableClearable?: boolean
  required?: boolean
  multiple?: boolean
}

const minWidth = { minWidth: '30rem' }

const autocompleteIndicatorButtonProps = {
  sx: {
    marginRight: 0,
    padding: 0,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  style: {
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 0,
    height: 'auto',
    minWidth: 0,
    width: 'auto',
  },
} as const

export const autocompletePopupIndicatorSlotProps = {
  popupIndicator: autocompleteIndicatorButtonProps,
  clearIndicator: autocompleteIndicatorButtonProps,
} as const

const AutoCompleteField: React.FC<AutoCompleteFieldProps> = ({
  name,
  label,
  options = [],
  required = false,
  multiple = false,
  ...props
}) => {
  const [field, , helpers] = useField({ name })
  const { setValue } = helpers

  const onChange = React.useCallback(
    (event, nextSelectedOption) => {
      if (multiple) setValue(nextSelectedOption)
      else setValue(nextSelectedOption?.value)
    },
    [setValue, multiple]
  )

  const getOptionLabel = React.useCallback(
    (option) => option?.label || options.find((o) => option === o.value)?.label || '',
    [options]
  )

  const getOptionKey = React.useCallback((option: listOption) => String(option?.value ?? option?.label ?? ''), [])

  return (
    <Autocomplete
      {...props}
      value={field.value}
      sx={{ minWidth }}
      options={options}
      multiple={multiple}
      disableCloseOnSelect={multiple}
      getOptionLabel={getOptionLabel}
      getOptionKey={getOptionKey}
      isOptionEqualToValue={(option, value) => !isNil(option) && option.value === value}
      onChange={onChange}
      slotProps={autocompletePopupIndicatorSlotProps}
      renderInput={(params) => (
        <TextField
          {...params}
          required={required}
          label={label}
          variant="outlined"
          size="small"
          margin="normal"
          fullWidth
        />
      )}
    />
  )
}

export default AutoCompleteField
